import React, { useState } from 'react';
import styled from 'styled-components';
import { stripePromise } from '../api/stripe';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { logout } from '../reducers/authSlice';
import { useNavigate } from 'react-router-dom';
import NotificationModal from '../components/NotificationModal';
import CookiesModal from '../components/CookiesModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import BuyPremium from '../components/BuyPremium';


const AccountContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 600px;
    margin: 10px auto;
    padding: 20px;
    background-color: #f8f9fa;
`;

const PremiumButton = styled.button`
    background-color: #f56607;
    color: white;
    padding: 15px;
    border-radius: 10px;
    border: none;
    width: 100%;
    font-size: 16px;
    font-weight: bold;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    cursor: pointer;
`;

const FullWidthButton = styled.button`
    background-color: #fff;
    color: #333;
    padding: 15px;
    border-radius: 10px;
    border: 1px solid #ddd;
    width: 100%;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    margin-bottom: 20px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    &:hover {
        background-color: #f0f0f0;
    }
`;

const LogoutButton = styled(FullWidthButton)`
    background-color: #ff4d4d;
    color: white;

    &:hover {
        background-color: #ff0000;
    }
`;

/** Owner-only entries. Brand orange so they read as admin actions,
 *  not user settings. */
const AdminButton = styled(FullWidthButton)`
    background-color: #f56607;
    color: #fff;
    border-color: #f56607;

    &:hover {
        background-color: #d95a06;
    }
`;

const Divider = styled.div`
    width: 80%;
    height: 1px;
    background-color: #ccc;
    margin: 20px 0;
`;

const SmallLinkContainer = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

const SmallLink = styled.a`
    font-size: 0.8em;
    color: #007bff;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const Account: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isPremiumModalOpen, setPremiumModalOpen] = useState(false);
    const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);
    const [isCookiesModalOpen, setCookiesModalOpen] = useState(false);
    const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);

    const user = useSelector((state: RootState) => state.auth.user);
    const accessToken = useSelector((state: RootState) => state.auth.accessToken);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

    const handleLogout = () => {
        dispatch(logout());
    };

    const openPremiumModal = () => {
        setPremiumModalOpen(true);
    };

    const closePremiumModal = () => {
        setPremiumModalOpen(false);
    };

    const openNotificationModal = () => {
        setNotificationModalOpen(true);
    };

    const closeNotificationModal = () => {
        setNotificationModalOpen(false);
    };

    const openCookiesModal = () => {
        setCookiesModalOpen(true);
    };

    const closeCookiesModal = () => {
        setCookiesModalOpen(false);
    };

    const openDeleteAccountModal = () => {
        setDeleteAccountModalOpen(true);
    };

    const closeDeleteAccountModal = () => {
        setDeleteAccountModalOpen(false);
    };

    return (
        <AccountContainer>
            {user?.isPremium ? (
                <PremiumButton disabled>Prémium Aktivované</PremiumButton>
            ) : (
                <>
                    <PremiumButton onClick={openPremiumModal}>KÚPIŤ PRÉMIUM</PremiumButton>
                </>
            )}
            <PremiumButton onClick={() => navigate('/predplatne')}>Členstvo</PremiumButton>
            <FullWidthButton onClick={() => navigate('/aplikacia')}>Stiahnuť Aplikáciu</FullWidthButton>
            <Divider />
            <FullWidthButton onClick={() => navigate('/profile-settings')}>Nastavenia profilu</FullWidthButton>
            <FullWidthButton onClick={openNotificationModal}>Upozornenia</FullWidthButton>
            {user?.isAdmin && (
                <AdminButton onClick={() => navigate('/admin')}>Správa používateľov (správca)</AdminButton>
            )}
            {/* Owner-only. The /faktury route and the API enforce this independently. */}
            {user?.isAdmin && (
                <AdminButton onClick={() => navigate('/faktury')}>Faktúry (správca)</AdminButton>
            )}
            {user?.isAdmin && (
                <AdminButton onClick={() => navigate('/kampane')}>E-mailové kampane (správca)</AdminButton>
            )}
            {user?.isAdmin && (
                <AdminButton onClick={() => navigate('/bezpecnost')}>Bezpečnosť (správca)</AdminButton>
            )}
            <Divider />
            {isAuthenticated && (
                <LogoutButton onClick={handleLogout}>Odhlásiť sa</LogoutButton>
            )}
            <SmallLinkContainer>
                <SmallLink href="/podmienky" target="_blank" rel="noopener noreferrer">Podmienky používania</SmallLink>
                <SmallLink onClick={openCookiesModal}>Nastavenia cookies</SmallLink>
                <SmallLink onClick={openDeleteAccountModal}>Odstraniť účet</SmallLink>
            </SmallLinkContainer>
            <BuyPremium
                key={`${user?.id}:${accessToken}`}
                isOpen={isPremiumModalOpen}
                onClose={closePremiumModal}
                stripePromise={stripePromise}
            />
            <NotificationModal
                isOpen={isNotificationModalOpen}
                onRequestClose={closeNotificationModal}
                onSettingsUpdate={() => { /* Handle settings update here */ }}
            />
            <CookiesModal
                isOpen={isCookiesModalOpen}
                onRequestClose={closeCookiesModal}
            />
            {/* The modal owns the call and the sign-out. A second copy of that logic used to sit
                here as `handleDeleteAccount`, passed in as `onDeleteAccount` — which the modal never
                invoked, so nothing ran it and nobody noticed that it pointed at
                `/account/delete/{id}`, a path this API has never had. Deleted rather than wired up:
                one of the two had to go, and the dead one was the broken one. */}
            <DeleteAccountModal
                isOpen={isDeleteAccountModalOpen}
                onRequestClose={closeDeleteAccountModal}
            />
        </AccountContainer>
    );
};

export default Account;
